import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import {
  focusGroupClassName,
  focusableClassName,
} from '../helpers/spatialFocus';
import { useAppSettings } from '../hooks/useAppSettings';
import { classNames } from '../lib/classNames';
import { triggerMediaTailorInjection } from '../lib/mediaTailor';
import { updateSearchParam } from '../lib/settings';
import type { PlayerType } from '../types/app';
import { BackButton } from '../pages/Channel/BackButton';

interface MemoryUsage {
  limit: number | null;
  percent: number | null;
  total: number | null;
  used: number | null;
}

const getMemoryUsage = (): MemoryUsage => {
  const memory = performance.memory;
  if (!memory) return { limit: null, percent: null, total: null, used: null };

  return {
    used: Number((memory.usedJSHeapSize / 1e6).toFixed(1)),
    total: Number((memory.totalJSHeapSize / 1e6).toFixed(1)),
    limit: Number((memory.jsHeapSizeLimit / 1e9).toFixed(2)),
    percent: memory.totalJSHeapSize
      ? Number(
          ((memory.usedJSHeapSize / memory.totalJSHeapSize) * 100).toFixed(1),
        )
      : null,
  };
};

const ToolButton = ({
  children,
  focusKey,
  onClick,
}: {
  children: ReactNode;
  focusKey: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    className={`dev-tool-button ${focusableClassName}`}
    data-focus-key={focusKey}
    onClick={onClick}
  >
    {children}
  </button>
);

const PlayerSelector = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { playerType } = useAppSettings();
  const [open, setOpen] = useState(false);
  const players: Array<{ label: string; value: PlayerType }> = [
    { label: 'HTML5', value: 'native' },
    { label: 'Video.js', value: 'videojs' },
    { label: 'Bitmovin', value: 'bitmovin' },
    { label: 'Shaka', value: 'shaka' },
    { label: 'Shaka + MT', value: 'shaka-mt' },
  ];

  const selectPlayer = (value: PlayerType) => {
    navigate(
      `${location.pathname}${updateSearchParam(location.search, 'player', value)}`,
      { replace: true, state: location.state },
    );
    setOpen(false);
  };

  return (
    <div className="relative">
      <ToolButton
        focusKey="tools:player"
        onClick={() => setOpen((value) => !value)}
      >
        Player:{' '}
        {players.find(({ value }) => value === playerType)?.label || playerType}
      </ToolButton>
      {open && (
        <div className={`dev-panel right-0 w-64 ${focusGroupClassName}`}>
          <h2 className="mb-3 text-lg font-semibold">Select player</h2>
          {players.map(({ label, value }, index) => (
            <button
              key={value}
              type="button"
              className={classNames(
                'dev-option',
                focusableClassName,
                index > 0 && 'mt-2',
                value === playerType && 'dev-option-active',
              )}
              data-focus-key={`tools:player:${value}`}
              onClick={() => selectPlayer(value)}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const InjectButton = () => {
  const [flexiOpen, setFlexiOpen] = useState(false);
  const [injecting, setInjecting] = useState(false);

  useEffect(() => {
    const handleOpen = () => setFlexiOpen(true);
    const handleClose = () => setFlexiOpen(false);
    window.addEventListener('flexiDidOpen', handleOpen);
    window.addEventListener('flexiDidClose', handleClose);
    return () => {
      window.removeEventListener('flexiDidOpen', handleOpen);
      window.removeEventListener('flexiDidClose', handleClose);
    };
  }, []);

  const inject = () => {
    setInjecting(true);
    triggerMediaTailorInjection()
      .catch((error: unknown) =>
        console.error('MediaTailor injection failed', error),
      )
      .finally(() => setInjecting(false));
  };

  return (
    <button
      type="button"
      className={`dev-tool-button ${focusableClassName}`}
      data-focus-key="tools:inject"
      disabled={flexiOpen || injecting}
      onClick={inject}
    >
      Inject
    </button>
  );
};

const DeviceInfo = () => {
  const [open, setOpen] = useState(false);
  const [memory, setMemory] = useState(getMemoryUsage);

  useEffect(() => {
    if (!open) return;
    const interval = window.setInterval(
      () => setMemory(getMemoryUsage()),
      1000,
    );
    return () => window.clearInterval(interval);
  }, [open]);

  const rows = [
    ['Screen', `${window.screen.width} × ${window.screen.height}`],
    ['CPU cores', String(navigator.hardwareConcurrency || 'Unavailable')],
    [
      'Device memory',
      navigator.deviceMemory ? `${navigator.deviceMemory} GB` : 'Unavailable',
    ],
    [
      'Network',
      navigator.connection?.downlink
        ? `${navigator.connection.downlink} Mbps`
        : 'Unavailable',
    ],
    ['Heap used', memory.used === null ? 'Unavailable' : `${memory.used} MB`],
    [
      'Heap total',
      memory.total === null ? 'Unavailable' : `${memory.total} MB`,
    ],
    [
      'Heap limit',
      memory.limit === null ? 'Unavailable' : `${memory.limit} GB`,
    ],
    [
      'Heap used %',
      memory.percent === null ? 'Unavailable' : `${memory.percent}%`,
    ],
  ];

  return (
    <div className="relative">
      <ToolButton
        focusKey="tools:device-info"
        onClick={() => setOpen((value) => !value)}
      >
        {open ? 'Hide device info' : 'Device info'}
      </ToolButton>
      {open && (
        <div className="dev-panel dev-panel-wide right-0">
          <h2 className="dev-heading mb-3 text-lg font-semibold">
            Device information
          </h2>
          <dl>
            {rows.map(([label, value]) => (
              <div key={label} className="dev-row flex py-2">
                <dt className="dev-label w-40 shrink-0 font-semibold">
                  {label}
                </dt>
                <dd className="min-w-0 break-all">{value}</dd>
              </div>
            ))}
            <div className="dev-row py-2">
              <dt className="dev-label font-semibold">User agent</dt>
              <dd className="mt-1 break-all text-sm">{navigator.userAgent}</dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  );
};

type LoadType = 'cpu' | 'memory';

const PerformanceEmulator = () => {
  const [open, setOpen] = useState(false);
  const [loadType, setLoadType] = useState<LoadType>('cpu');
  const [intensity, setIntensity] = useState(50);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const allocationsRef = useRef<number[][]>([]);

  const stop = () => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
    }
    intervalRef.current = null;
    allocationsRef.current = [];
    setRunning(false);
  };

  useEffect(() => stop, []);

  const start = () => {
    stop();
    setRunning(true);

    intervalRef.current = window.setInterval(() => {
      if (loadType === 'cpu') {
        const startTime = Date.now();
        while (Date.now() - startTime < intensity * 3) {
          // Intentionally busy for the diagnostics tool.
        }
      } else {
        allocationsRef.current.push(
          new Array<number>(1024 * intensity * 10).fill(Math.random()),
        );
        if (allocationsRef.current.length > 1000) {
          allocationsRef.current.shift();
        }
      }
    }, 300);
  };

  return (
    <div className="relative">
      <ToolButton
        focusKey="tools:load"
        onClick={() => {
          if (open && running) stop();
          setOpen((value) => !value);
        }}
      >
        {open ? 'Hide load tool' : 'Load tool'}
      </ToolButton>
      {open && (
        <div className={`dev-panel left-0 w-80 ${focusGroupClassName}`}>
          <h2 className="mb-3 text-lg font-semibold">Performance load</h2>
          <div className="flex">
            {(['cpu', 'memory'] as LoadType[]).map((value, index) => (
              <button
                key={value}
                type="button"
                className={classNames(
                  'dev-option',
                  focusableClassName,
                  index > 0 && 'ml-2',
                  value === loadType && 'dev-option-active',
                )}
                disabled={running}
                data-focus-key={`tools:load:${value}`}
                onClick={() => setLoadType(value)}
              >
                {value === 'cpu' ? 'CPU' : 'Memory'}
              </button>
            ))}
          </div>
          <label className="mt-4 block">
            Intensity: {intensity}
            <input
              className="mt-2 w-full"
              type="range"
              min="20"
              max="100"
              step="10"
              value={intensity}
              disabled={running}
              onChange={(event) => setIntensity(Number(event.target.value))}
            />
          </label>
          <div className="mt-4 flex">
            <button
              type="button"
              className={`dev-action dev-action-start ${focusableClassName}`}
              data-focus-key="tools:load:start"
              disabled={running}
              onClick={start}
            >
              Start
            </button>
            <button
              type="button"
              className={`dev-action dev-action-stop ml-3 ${focusableClassName}`}
              data-focus-key="tools:load:stop"
              disabled={!running}
              onClick={stop}
            >
              Stop
            </button>
          </div>
          <p className="mt-4">Status: {running ? 'Running' : 'Stopped'}</p>
        </div>
      )}
    </div>
  );
};

export const DevTools = () => {
  const channelRoute = useMatch('/:env/:accountId/:channelId');
  const location = useLocation();
  const navigate = useNavigate();
  const { playerType, testingMode } = useAppSettings();
  const showInjectButton = Boolean(channelRoute) && playerType === 'shaka-mt';
  const showBackButton =
    window.self === window.top &&
    (!channelRoute || Boolean(location.state?.channel));

  const goBack = () => navigate(-1);

  if (window.self === window.top && testingMode) {
    return (
      <aside
        className={`pointer-events-none absolute left-4 right-4 top-4 z-[200000] flex justify-end ${focusGroupClassName}`}
      >
        <div className="pointer-events-auto flex">
          {showBackButton && (
            <div className="mr-3">
              <BackButton onClick={goBack} />
            </div>
          )}
          {showInjectButton && (
            <div className="mr-3">
              <InjectButton />
            </div>
          )}
          <div className="mr-3">
            <PlayerSelector />
          </div>
          <div className="mr-3">
            <PerformanceEmulator />
          </div>
          <DeviceInfo />
        </div>
      </aside>
    );
  }

  return (
    <aside
      className={`pointer-events-none absolute left-4 right-4 top-4 z-[200000] flex justify-end ${focusGroupClassName}`}
    >
      <div className="pointer-events-auto flex">
        {showBackButton && (
          <div className="mr-3">
            <BackButton onClick={goBack} />
          </div>
        )}
        {showInjectButton && (
          <div className="mr-3">
            <InjectButton />
          </div>
        )}
        <PlayerSelector />
      </div>
    </aside>
  );
};
